/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';

export type NavigationLink = { label: string; name: string };

type Props = {
  navigationLinks?: Array<NavigationLink>;
};

const styles = {
  navItems: css`
    list-style: none;
    padding-left: 0;
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    margin: 0;
    gap: 32px;
    padding: var(--cds-spacing-150) 0;
    ${breakpoints.down('sm')} {
      padding-top: var(--cds-spacing-100);
    }

    /* hide scrollbar for Firefox */
    scrollbar-width: none;

    /* hide scrollbar for Chrome, Safari and Edge */
    ::-webkit-scrollbar {
      display: none;
    }
  `,
  navLink: (theme: Theme) => css`
    display: block;
    border-radius: 4px;
    color: var(--cds-color-neutral-primary);
    font-family: ${theme.typography.fontFamily};
    padding: 10px 12px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;

    :hover,
    :focus {
      text-decoration: none;
      color: var(--cds-color-blue-800);
      background: var(--cds-color-blue-25);
    }

    :focus {
      outline: 1px solid var(--cds-color-blue-700);
      outline-offset: -1px;
    }
  `,
  activeNavLink: css`
    color: var(--cds-color-blue-700);
    background: var(--cds-color-grey-25);
    text-decoration: underline;
  `,
};

const PageNavigation = ({ navigationLinks }: Props) => {
  const { sectionInView } = usePageNavigation();

  if (!navigationLinks || navigationLinks.length === 0) {
    return null;
  }

  return (
    <nav>
      <ul css={styles.navItems}>
        {navigationLinks.map(({ name, label }) => {
          const isActiveItem = sectionInView === name;
          return (
            <li key={name}>
              <TrackedA
                href={`#${name}`}
                trackingName={`nav_item_${name}`}
                css={[styles.navLink, isActiveItem && styles.activeNavLink]}
                {...(isActiveItem && { 'aria-current': true })}
              >
                {label}
              </TrackedA>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default PageNavigation;
